import {
  Button,
  Checkbox,
  Icon,
  RadioButton,
  TextField,
} from "@shopify/polaris";
import {
  EmailIcon,
  ImageIcon,
  ProductIcon,
  RefreshIcon,
  TextAlignLeftIcon,
  VariantIcon,
} from "@shopify/polaris-icons";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const SyncSettings = () => {
  const [email, setEmail] = useState("");
  const location = useLocation();

  const [productSubmittedFeed, setProductSubmittedFeed] = useState("all");
  const [productTitle, setProductTitle] = useState("default");
  const [productDescription, setProductDescription] = useState("default_desc");
  const [variantSubmission, setVariantSubmission] = useState("all_var");
  const [appendVariantName, setAppendVariantName] = useState("no");
  const [inventoryPolicy, setInventoryPolicy] = useState("ignore");
  const [submitAdditionalImages, setSubmitAdditionalImages] = useState(false);
  const [richProductDescription, setRichProductDescription] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleEmailChange = useCallback((newValue) => setEmail(newValue), []);
  const handleProductFeedChange = useCallback(
    (_, newValue) => setProductSubmittedFeed(newValue),
    [],
  );
  const handleProductTitleChange = useCallback(
    (_, newValue) => setProductTitle(newValue),
    [],
  );
  const handleProductDescriptionChange = useCallback(
    (_, newValue) => setProductDescription(newValue),
    [],
  );
  const handleVariantSubmissionChange = useCallback(
    (_, newValue) => setVariantSubmission(newValue),
    [],
  );
  const handleAppendVariantNameChange = useCallback(
    (_, newValue) => setAppendVariantName(newValue),
    [],
  );

  const fetchAccountStatus = async (shop) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/google/auth/account/statuses?shop=${shop}`,
      );
      const data = await response.json();
      if (data.data) {
        setAccountStatus(data);
      }
    } catch (error) {
      console.error("Error fetching account status:", error);
    }
  };

  const saveSettings = async () => {
    const shop = new URLSearchParams(location.search).get("shop");
    const settings = {
      syncSettings: {
        email,
        productSubmittedFeed,
        productTitle,
        productDescription,
        variantSubmission,
        appendVariantName,
        inventoryPolicy,
        submitAdditionalImages,
        richProductDescription,
      },
    };

    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/shopify/store/update_settings?shop=${shop}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ settings }),
        },
      );

      const result = await response.json();
      if (response.ok) {
        alert("Settings saved successfully!");
      } else {
        console.error("Error saving settings:", result);
        alert("Failed to save settings!");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("An error occurred while saving settings.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const shop = new URLSearchParams(location.search).get("shop");
    const fetchSettings = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/shopify/store/settings?shop=${shop}`,
        );
        if (!response.ok) throw new Error("Failed to fetch settings");

        const data = await response.json();
        const syncSettings = data.data;
        if (syncSettings?.syncSettings) {
          setEmail(syncSettings.syncSettings?.email || "");
          setProductSubmittedFeed(
            syncSettings.syncSettings?.productSubmittedFeed || "all",
          );
          setProductTitle(syncSettings.syncSettings?.productTitle || "default");
          setProductDescription(
            syncSettings.syncSettings?.productDescription || "default_desc",
          );
          setVariantSubmission(
            syncSettings.syncSettings?.variantSubmission || "first_var",
          );
          setAppendVariantName(
            syncSettings.syncSettings?.appendVariantName || "yes",
          );
          setInventoryPolicy(
            syncSettings.syncSettings?.inventoryPolicy || "ignore",
          );
          setSubmitAdditionalImages(
            syncSettings.syncSettings?.submitAdditionalImages || false,
          );
          setRichProductDescription(
            syncSettings.syncSettings?.richProductDescription || false,
          );
        }
        await fetchAccountStatus(shop);
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };

    fetchSettings();
  }, [location.search]);

  const activeProducts =
    accountStatus?.accountStatus?.products?.[0]?.statistics.active || 0;

  return (
    <div className="settings-card">
      <div className="settings-card__header">
        <span className="app-icon-tile">
          <Icon source={RefreshIcon} tone="base" />
        </span>
        <div>
          <h3>Sync Settings From Shopify</h3>
          <p>Choose what gets pulled from Shopify into your Google feed.</p>
        </div>
      </div>

      <div className="settings-field">
        <div className="settings-field__label">Total Products Published</div>
        <div className="settings-field__control">
          <span className="settings-stat">
            <Icon source={ProductIcon} tone="base" />
            {activeProducts} published
          </span>
        </div>
      </div>

      <div className="settings-field">
        <div className="settings-field__label">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Icon source={EmailIcon} tone="base" />
            Notification Email
          </span>
        </div>
        <div className="settings-field__control" style={{ width: "100%" }}>
          <div style={{ width: "100%", maxWidth: 360 }}>
            <TextField
              label="Notification Email"
              labelHidden
              value={email}
              onChange={handleEmailChange}
              autoComplete="off"
              placeholder="ops@yourstore.com"
            />
          </div>
        </div>
      </div>

      <div className="settings-field">
        <div className="settings-field__label">
          Which products need to be submitted?
        </div>
        <div className="settings-field__control">
          <RadioButton
            label="All Products"
            id="all"
            checked={productSubmittedFeed === "all"}
            name="productSubmittedFeed"
            onChange={handleProductFeedChange}
          />
          <RadioButton
            label="Products from a Collection"
            id="collection"
            name="productSubmittedFeed"
            checked={productSubmittedFeed === "collection"}
            onChange={handleProductFeedChange}
          />
        </div>
      </div>

      <div className="settings-field">
        <div className="settings-field__label">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Icon source={TextAlignLeftIcon} tone="base" />
            Product Title Preference
          </span>
        </div>
        <div className="settings-field__control">
          <RadioButton
            label="Default Product Title"
            id="default"
            name="productTitle"
            checked={productTitle === "default"}
            onChange={handleProductTitleChange}
          />
          <RadioButton
            label="SEO Title"
            id="seo"
            name="productTitle"
            checked={productTitle === "seo"}
            onChange={handleProductTitleChange}
          />
        </div>
      </div>

      <div className="settings-field">
        <div className="settings-field__label">
          Product Description Preference
        </div>
        <div className="settings-field__control">
          <RadioButton
            label="Default Product Description"
            id="default_desc"
            name="productDescription"
            checked={productDescription === "default_desc"}
            onChange={handleProductDescriptionChange}
          />
          <RadioButton
            label="SEO Description"
            id="seo_desc"
            name="productDescription"
            checked={productDescription === "seo_desc"}
            onChange={handleProductDescriptionChange}
          />
        </div>
      </div>

      <div className="settings-field">
        <div className="settings-field__label">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Icon source={VariantIcon} tone="base" />
            Variant Submission Preference
          </span>
        </div>
        <div className="settings-field__control">
          <RadioButton
            label="First Variant Only"
            id="first_var"
            name="variantSubmission"
            checked={variantSubmission === "first_var"}
            onChange={handleVariantSubmissionChange}
          />
          <RadioButton
            label="All Variants"
            id="all_var"
            name="variantSubmission"
            checked={variantSubmission === "all_var"}
            onChange={handleVariantSubmissionChange}
          />
        </div>
      </div>

      <div className="settings-field">
        <div className="settings-field__label">
          Append variant name to product title?
        </div>
        <div className="settings-field__control">
          <RadioButton
            label="Yes"
            id="yes"
            name="appendVariantName"
            checked={appendVariantName === "yes"}
            onChange={handleAppendVariantNameChange}
          />
          <RadioButton
            label="No"
            id="no"
            name="appendVariantName"
            checked={appendVariantName === "no"}
            onChange={handleAppendVariantNameChange}
          />
        </div>
      </div>

      <div className="settings-field">
        <div className="settings-field__label">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Icon source={ImageIcon} tone="base" />
            Submit Additional Images
          </span>
        </div>
        <div className="settings-field__control">
          <Checkbox
            label="Include additional images in the Google feed"
            checked={submitAdditionalImages}
            onChange={() =>
              setSubmitAdditionalImages((current) => !current)
            }
          />
        </div>
      </div>

      <div className="settings-actions">
        <Button
          variant="primary"
          icon={RefreshIcon}
          loading={saving}
          onClick={saveSettings}
        >
          Re-Sync
        </Button>
      </div>
    </div>
  );
};

export default SyncSettings;
